import axios from "axios";

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "",
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Response interceptor for 401 handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect if not already on auth pages
      if (!window.location.pathname.startsWith("/login") && !window.location.pathname.startsWith("/signup") && !window.location.pathname.startsWith("/register")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// --- Auth API ---
export const authApi = {
  login: (data: { email: string; password: string }) =>
    api.post("/api/auth/login", data),
  register: (data: { email: string; password: string; name?: string; username?: string }) =>
    api.post("/api/auth/signup", data),
  logout: () => api.post("/api/auth/logout"),
  me: () => api.get("/api/auth/me"),
  otpSend: (data: { phone: string }) => api.post("/api/auth/otp/send", data),
  otpVerify: (data: { phone: string; code: string }) =>
    api.post("/api/auth/otp/verify", data),
  otpCheck: (phone: string) => api.get("/api/auth/otp/check", { params: { phone } }),
};

// --- Wallet API ---
export const walletApi = {
  getBalance: () => api.get("/api/wallet/balance"),
  getTransactions: (params?: Record<string, unknown>) =>
    api.get("/api/wallet/transactions", { params }),
  getPayments: (params?: Record<string, unknown>) =>
    api.get("/api/wallet/payments", { params }),
  deposit: (amount: number) => api.post("/api/wallet/deposit", { amount }),
  getDepositInfo: () => api.get("/api/wallet/deposit-info"),
};

// --- API Keys API ---
export const apiKeysApi = {
  list: () => api.get("/api/keys"),
  create: (data: { name: string; credit_limit?: number | null }) =>
    api.post("/api/keys", data),
  update: (id: string | number, data: { name?: string; status?: string; credit_limit?: number | null }) =>
    api.patch(`/api/keys/${id}`, data),
  revoke: (id: string) => api.delete(`/api/keys/${id}`),
};

// --- Usage API ---
export const usageApi = {
  getLogs: (params?: Record<string, unknown>) =>
    api.get("/api/users/logs", { params }),
  getSummary: (params?: Record<string, unknown>) =>
    api.get("/api/usage/summary", { params }),
  getFilters: () => api.get("/api/usage/filters"),
  getTimeseries: (params?: Record<string, unknown>) =>
    api.get("/api/usage/timeseries", { params }),
  getGroup: (params?: Record<string, unknown>) =>
    api.get("/api/usage/group", { params }),
  getStacked: (params?: Record<string, unknown>) =>
    api.get("/api/usage/stacked", { params }),
  getDashboard: (params?: Record<string, unknown>) =>
    api.get("/api/usage/dashboard", { params }),
  getActivity: () => api.get("/api/usage/activity"),
  getSankeyKeys: (params?: Record<string, unknown>) =>
    api.get("/api/usage/sankey-keys", { params }),
  getSankeyModels: (params?: Record<string, unknown>) =>
    api.get("/api/usage/sankey-models", { params }),
};

// --- Admin API ---
export const adminApi = {
  login: (data: { email: string; password: string }) =>
    api.post("/api/admin/login", data),
  recoverySend: (data: { identifier: string }) =>
    api.post("/api/admin/recovery/send", data),
  recoveryVerify: (data: { identifier: string; code: string }) =>
    api.post("/api/admin/recovery/verify", data),
  logout: () => api.post("/api/auth/logout"),
  me: () => api.get("/api/admin/me"),
  getUsers: (params?: Record<string, unknown>) =>
    api.get("/api/admin/users", { params }),
  updateUser: (id: string, data: Record<string, unknown>) =>
    api.put(`/api/admin/users/${id}`, data),
  deleteUser: (id: string) => api.delete(`/api/admin/users/${id}`),
  updateUserSubscription: (id: string, data: Record<string, unknown>) =>
    api.put(`/api/admin/users/${id}/subscription`, data),
  getModels: (params?: Record<string, unknown>) =>
    api.get("/api/admin/models", { params }),
  getModelDetail: (id: string | number) =>
    api.get(`/api/admin/models/${id}/detail`),
  updateModel: (id: string, data: Record<string, unknown>) =>
    api.put(`/api/admin/models/${id}`, data),
  getStats: () => api.get("/api/admin/stats"),
  getNotifications: () => api.get("/api/admin/notifications"),
  getLogs: (params?: Record<string, unknown>) =>
    api.get("/api/admin/logs", { params }),
  getLogFilters: () => api.get("/api/admin/logs/filters"),
  recoverLog: (id: number, data?: { openrouter_generation_id?: string }) =>
    api.post(`/api/admin/logs/${id}/recover`, data ?? {}),
  recoverStuckLogs: (limit = 100) =>
    api.post("/api/admin/logs/recover-stuck", null, { params: { limit } }),
  deleteLog: (id: number) => api.delete(`/api/admin/logs/${id}`),
  failLog: (id: number, data?: { reason?: string }) =>
    api.post(`/api/admin/logs/${id}/fail`, data ?? {}),
  getSystem: () => api.get("/api/admin/system"),
  getTotalResources: () => api.get("/api/admin/system/resources"),
  getServiceResources: (service: string) =>
    api.get(`/api/admin/system/${encodeURIComponent(service)}/resources`),
  getSettings: () => api.get("/api/admin/settings"),
  updateSettings: (data: Record<string, unknown>) =>
    api.put("/api/admin/settings", data),
  getTickets: (params?: Record<string, unknown>) =>
    api.get("/api/admin/tickets", { params }),
  getTicket: (ticketId: number) => api.get(`/api/admin/tickets/${ticketId}`),
  replyTicket: (ticketId: number, data: { message: string }) =>
    api.post(`/api/admin/tickets/${ticketId}/reply`, data),
  closeTicket: (ticketId: number) =>
    api.post(`/api/admin/tickets/${ticketId}/close`),
  reopenTicket: (ticketId: number) =>
    api.post(`/api/admin/tickets/${ticketId}/reopen`),
  getRoles: () => api.get("/api/admin/roles"),
  createRole: (data: Record<string, unknown>) =>
    api.post("/api/admin/roles", data),
  updateRole: (id: string, data: Record<string, unknown>) =>
    api.put(`/api/admin/roles/${id}`, data),
  deleteRole: (id: string) => api.delete(`/api/admin/roles/${id}`),
  getExpenses: () => api.get("/api/admin/expenses"),
  createExpense: (data: { amount: number; description: string }) =>
    api.post("/api/admin/expenses", data),
  updateExpense: (id: number, data: { amount: number; description: string }) =>
    api.put(`/api/admin/expenses/${id}`, data),
  getPlans: () => api.get("/api/admin/plans"),
  getPlanModels: () => api.get("/api/admin/plans/models"),
  getCodecraftPlanModels: () => api.get("/api/admin/plans/codecraft-models"),
  getPlanUsers: (search: string, ids: number[]) =>
    api.get("/api/admin/plans/users", {
      params: { search, ids: ids.join(",") },
    }),
  getUserUsage: (userId: number) => api.get(`/api/admin/users/${userId}/usage`),
  createPlan: (data: Record<string, unknown>) =>
    api.post("/api/admin/plans", data),
  updatePlan: (id: string, data: Record<string, unknown>) =>
    api.put(`/api/admin/plans/${id}`, data),
  deletePlan: (id: string) => api.delete(`/api/admin/plans/${id}`),
  getMessages: () => api.get("/api/admin/messages"),
  createMessage: (data: Record<string, unknown>) =>
    api.post("/api/admin/messages", data),
  updateMessage: (id: number, data: Record<string, unknown>) =>
    api.put(`/api/admin/messages/${id}`, data),
  deleteMessage: (id: number) => api.delete(`/api/admin/messages/${id}`),
  getPartners: (params?: Record<string, unknown>) =>
    api.get("/api/admin/partners", { params }),
  createPartner: (data: { name: string; code: string; initial_balance?: number; currency?: string; plan_id?: number | null; representative_user_id?: number | null }) =>
    api.post("/api/admin/partners", data),
  getPartner: (id: number) => api.get(`/api/admin/partners/${id}`),
  getPartnerUsers: (id: number) => api.get(`/api/admin/partners/${id}/users`),
  updatePartner: (id: number, data: Record<string, unknown>) =>
    api.patch(`/api/admin/partners/${id}`, data),
  deletePartner: (id: number) => api.delete(`/api/admin/partners/${id}`),
  listPartnerKeys: (id: number) => api.get(`/api/admin/partners/${id}/keys`),
  createPartnerKey: (id: number) =>
    api.post(`/api/admin/partners/${id}/keys`),
  disablePartnerKey: (partnerId: number, keyId: number) =>
    api.post(`/api/admin/partners/${partnerId}/keys/${keyId}/disable`),
  getFactors: (params?: Record<string, unknown>) =>
    api.get("/api/admin/factors", { params }),
};

// --- Profile API ---
export const profileApi = {
  get: () => api.get("/api/profile"),
  update: (data: Record<string, unknown>) => api.put("/api/profile", data),
  changePassword: (data: Record<string, unknown>) => api.post("/api/profile/password", data),
  sendVerify: (field: string, value?: string) =>
    api.post("/api/profile/verify/send", value ? { field, value } : { field }),
  confirmVerify: (field: string, code: string) => api.post("/api/profile/verify/confirm", { field, code }),
};

// --- Subscription API ---
export const subscriptionApi = {
  getMe: () => api.get("/api/subscription/me"),
  getUsage: () => api.get("/api/subscription/usage"),
  getUsageTimeseries: (metric: "tokens" | "requests") =>
    api.get("/api/subscription/usage/timeseries", { params: { metric } }),
  getHistory: () => api.get("/api/subscription/history"),
  upgrade: (data: { plan_id: number; duration: number }) =>
    api.post("/api/subscription/upgrade", data),
};

// --- System Messages API (user-facing dashboard banners) ---
export const messagesApi = {
  getActive: (placement?: string) =>
    api.get("/api/messages", {
      params: placement ? { placement } : undefined,
    }),
};

// Enrich fixed-rate rows so the drawer never renders blank. The provider always
// publishes context_length/context_window, description, capabilities, etc. —
// when the DB cache is stale (pre-fix rows with nulls) we show a real fallback
// and let the celery sync backfill the cache. No guessed context lengths.
function enrichSubModel<T extends Record<string, unknown>>(raw: T): T {
  if (!raw || typeof raw !== "object") return raw;
  const out: Record<string, unknown> = { ...raw };
  const name = String((out as Record<string, unknown>).model_name || "");
  const display = String((out as Record<string, unknown>).display_name || name);
  // Keep provider description verbatim; only fill when truly empty so the
  // drawer copy (dir=rtl) has something to render.
  if (out.description == null || String(out.description).trim() === "") {
    out.description =
      `مدل ${display} از طریق API تعرفه ثابت (‎/sub/v1/chat/completions) قابل استفاده است. این مدل سازگار با OpenAI است و از استریم پشتیبانی می‌کند. شناسه را مستقیماً در درخواست استفاده کنید.`;
  }
  // Alias legacy field: some older payloads only had context_length.
  if ((out.context_window == null || out.context_window === "") && (out as Record<string, unknown>).context_length != null) {
    (out as Record<string, unknown>).context_window = (out as Record<string, unknown>).context_length;
  }
  if ((out as Record<string, unknown>).context_length == null && out.context_window != null) {
    (out as Record<string, unknown>).context_length = out.context_window;
  }
  // Do NOT invent a context length — show "—" until the DB is refreshed.
  // Strip the temporary _context_guessed flag added by the earlier hotfix.
  if ("_context_guessed" in out) delete (out as Record<string, unknown>)._context_guessed;

  const caps = Array.isArray(out.capabilities) ? (out.capabilities as unknown[]).map(String).map((c) => c.toLowerCase()) : [];
  if (caps.length === 0) {
    const needed = ["chat", "streaming", "tools", "reasoning", "function_calling"];
    for (const n of needed) if (!caps.includes(n)) caps.push(n);
    out.capabilities = caps;
  } else {
    out.capabilities = caps;
  }
  if (out.architecture == null && (out as Record<string, unknown>).architecture_modality == null && (out as Record<string, unknown>).input_modalities == null) {
    (out as Record<string, unknown>).architecture = "text → text";
    (out as Record<string, unknown>).architecture_modality = "text->text";
    (out as Record<string, unknown>).input_modalities = ["text"];
    (out as Record<string, unknown>).output_modalities = ["text"];
  }
  if (!out.input_modalities) (out as Record<string, unknown>).input_modalities = ["text"];
  if (!out.output_modalities) (out as Record<string, unknown>).output_modalities = ["text"];
  if (!out.type) out.type = "chat";
  if (!out.display_name) out.display_name = (out as Record<string, unknown>).model_name;
  if (!out.supported_parameters) out.supported_parameters = ["temperature", "max_tokens", "top_p", "stream", "tools", "tool_choice"];
  void name;
  return out as T;
}

// --- Fixed-rate (sub) API ---
export const subApi = {
  getModels: () =>
    api.get("/api/sub/models").then((r) => {
      if (Array.isArray(r.data)) r.data = (r.data as Record<string, unknown>[]).map(enrichSubModel);
      return r;
    }),
  getModelDetail: (modelId: string) =>
    api.get(`/api/sub/models/${encodeURIComponent(modelId)}`).then((r) => {
      if (r.data && typeof r.data === "object" && (r.data as Record<string, unknown>).model_name) {
        r.data = enrichSubModel(r.data as Record<string, unknown>);
      }
      return r;
    }),
};

// --- Support API ---
export const supportApi = {
  listTickets: () => api.get("/api/support/tickets"),
  getTicket: (ticketId: number) => api.get(`/api/support/tickets/${ticketId}`),
  createTicket: (data: Record<string, unknown>, files?: File[]) => {
    const { files: _ignored, ...fields } = data;
    if (files && files.length > 0) {
      const form = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        if (value !== undefined && value !== null) form.append(key, String(value));
      });
      files.forEach((file) => form.append("files", file));
      return api.postForm("/api/support/tickets", form);
    }
    return api.post("/api/support/tickets", fields);
  },
  replyTicket: (ticketId: number, data: { message: string }, files?: File[]) => {
    if (files && files.length > 0) {
      const form = new FormData();
      form.append("message", data.message);
      files.forEach((file) => form.append("files", file));
      return api.postForm(`/api/support/tickets/${ticketId}/reply`, form);
    }
    return api.post(`/api/support/tickets/${ticketId}/reply`, data);
  },
  closeTicket: (ticketId: number) => api.post(`/api/support/tickets/${ticketId}/close`),
  getNotifications: () => api.get("/api/support/notifications"),
  getLimits: () => api.get("/api/support/limits"),
  markTicketRead: (ticketId: number) => api.post(`/api/support/tickets/${ticketId}/read`),
  getAttachmentDownloadUrl: (ticketId: number, attachmentId: number) =>
    `/api/support/tickets/${ticketId}/attachments/${attachmentId}/download`,
};

// --- Config API ---
export const configApi = {
  getUsdRate: () => api.get("/api/config/usd-rate"),
};

// --- Models API ---
export const modelsApi = {
  list: (params?: Record<string, unknown>) =>
    api.get("/api/models", { params }),
  detail: (modelName: string) =>
    api.get(`/api/models/${modelName.split("/").map(encodeURIComponent).join("/")}`),
};

// --- Partner Representative API (read-only for keys; create/disable via B2B API) ---
export const partnerRepApi = {
  status: () => api.get("/api/partner-rep/status"),
  dashboard: () => api.get("/api/partner-rep/dashboard"),
  listUsers: (params?: Record<string, unknown>) =>
    api.get("/api/partner-rep/users", { params }),
  listUserKeys: (userId: number) => api.get(`/api/partner-rep/users/${userId}/keys`),
  userSummary: (userId: number, params?: Record<string, unknown>) =>
    api.get(`/api/partner-rep/users/${userId}/summary`, { params }),
  userTimeseries: (userId: number, params?: Record<string, unknown>) =>
    api.get(`/api/partner-rep/users/${userId}/timeseries`, { params }),
  userDashboard: (userId: number, params?: Record<string, unknown>) =>
    api.get(`/api/partner-rep/users/${userId}/dashboard`, { params }),
};
